<template>
    <div class="flex flex-col gap-5 min-h-96">
      <div class="flex gap-4">
        <div class="h-24 w-24 flex grow-0 shrink-0 border rounded-md">
          <NuxtImg :src="thumbnail" class="max-h-full max-w-full" quality="90" loading="lazy" @error="setDefaultImage" :alt="data.name" :title="data.name" />
        </div>
        <div class="flex flex-col gap-0.5">
          <p class="line-clamp-2">{{ data.name }}</p>
          <p class="text-sm line-clamp-2 text-gray-500">{{ data.detail.summary }}</p>
          <span class="text-md font-semibold" v-if="data.price > 0">&#8377; {{ parseFloat(data.price).toFixed(2) }}</span>
        </div>
      </div>
      <!-- <div class="flex justify-end">
        <span class="text-red-500 text-md font-bold" @click="clearAllSelections">
          clear all
        </span>
      </div> -->
      <div class="flex flex-col gap-4 h-48 overflow-y-scroll" v-if="!isFetchingCompleted">
        <div class="text-gray-500 text-md text-center mx-auto py-12">
          <UIcon name="i-heroicons-arrow-path" />
          Loading
        </div>
      </div>
      <div class="flex flex-col gap-4 min-h-48 max-h-96 overflow-y-scroll scrollbar-hide" v-else>
        <div class="flex flex-col gap-4 px-4">
            <div class="flex flex-col gap-2" v-for="variant in productVariants" :key="variant.label">
              <h6 class="text-md font-bold text-gray-900 pb-1">
                {{ useStartCase(variant.label == 'uom' ? 'Pack Size' : variant.label) }}
              </h6>
              <div class="flex flex-wrap gap-2">
                <!-- Normal Variant Buttons -->
                <UButton v-if="variant.label !== 'size_chart' && variant.label.toLowerCase() !== 'size chart'"
                  v-for="value in variant.values" :key="value" :class="{
                    'bg-primary text-white pointer-events-none': currentVariant[variant.label] === value,
                    'hover:bg-gray-100': currentVariant[variant.label] !== value
                  }" :style="getButtonStyle(variant, value, variant.label)"
                  @click="selectVariant(variant, value, variant.label)" :disabled="variantLength > 1 && isDisabled"
                  color="primary" variant="outline" class="px-2 py-1 text-xs">

                  <span v-if="variant.label !== 'colour'">{{ value }}</span>
                </UButton>

                <!-- Size Chart Button -->
                <UButton v-if="variant.label === 'size_chart' || variant.label.toLowerCase() === 'size chart'" :class="{
                  'hover:bg-gray-100': currentVariant[variant.label] !== value
                }" :style="getButtonStyle(variant, value, variant.label)"
                  @click="selectVariant(variant, value, variant.label)" :disabled="variantLength > 1 && isDisabled"
                  color="primary" variant="outline" class="px-2 py-1 text-xs">
                  <span class="text-blue-600 cursor-pointer underline" @click="showPopup(variant.values, index)">Click
                    here</span>
                </UButton>
              </div>
            </div>
          </div>
      </div>
      <div class="flex flex-row justify-between w-full md:justify-end items-center px-2">
        <div class="flex flex-col px-2 justify-between md:px-8 md:border-r-2 border-gray-300 grow-0 shrink-0">
          <h5 class="text-sm text-gray-500">Total</h5>
          <p class="text-md font-semibold">&#8377; {{ totalAmount }}</p>
        </div>
        <div class="flex justify-center w-full p-8">
          <V3CartButtonComponent :product="product" size="xl" label="Add To Cart" :disabled="isOutOfStock"/>
          <!-- <div
            class="flex justify-between items-center bg-gradient-to-r from-primary-600 to-shade-600 w-40 font-bold rounded-full text-white px-4 py-2"
             v-if="cartItemsData.length > 0 && cartItem?.qty > 0">
            <V3ImageComponent src="icons/minus.png" class="h-0.5 w-3 cursor-pointer"
              @click.stop="reduceQty(productSku || data.parent_item_id)" />
            <span>{{ cartItem.qty }}</span>
            <V3ImageComponent src="icons/plus.png" class="h-3 w-3 cursor-pointer" @click.stop="addToCart()" />
          </div>
          <UButton v-else class="w-40" color="primary" size="xl" block variant="outline" :ui="{ variant: { outline: 'bg-orange-50' } }" @click.stop="handleAddToCart">Add to Cart</UButton> -->
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
import shortHash from 'short-hash';
import { V3PopupComponent, V3ModalComponent } from '#components';
import { EventParameter } from '~/google_analytics/events.enum';
import { GoogleAnalytics } from '~/google_analytics/events';
const { isMobile } = useDevice()
const runtimeConfig = useRuntimeConfig();
const { gtag } = useGtag();
const auth = useAuth();
const isAuthenticated = auth && auth.access_token;
const userData = isAuthenticated
  ? {
      user_name: auth.user.first_name,
      user_phone: auth.user.phone,
      user_email: auth.user.email,
    }
  : {
      user_name: null,
      user_phone: null,
      user_email: null,
    };

const cart = useCart();
const props = defineProps(['data']);
const emit = defineEmits(['close']);

const isFetchingCompleted = ref(false);
const productSku = ref(props.data.sku_id);
const variants = ref([]);
const slideover = useSlideover();
const modal = useModal();
const product = ref({});
const { isPopupOpen, openPopup, closePopup } = popupBackHandler();

// const result = await useAPIHandler('get', `/products/${productSku.value}`);
// const product = result.data || {}; 

const fetchVariants = async (sku) => {
  isFetchingCompleted.value = false;
  const { data: apiResult } = await useAPIHandler('get', `/products/${sku}/variants`, {
    query: { partner_id: useRuntimeConfig().public.partnerCode },
  });
  variants.value = apiResult;
  const result = await useAPIHandler('get', `/products/${sku}`, {
      query: { partner_id: useRuntimeConfig().public.partnerCode },
    });
  product.value = result.data || {};
  isFetchingCompleted.value = true;
};

const productVariants = computed(() => variants.value?.[0]?.variants || []);

const currentVariant = computed(() => {
  return variants.value?.[0]?.products.find((item) => item.sku_id === productSku.value);
});

const isOutOfStock = product?.stock ? Number(product?.stock?.available) === 0 : false;

const selected = ref({});
const thumbnail = await useValidateThumbnailBuilder(props.data.images);

const setDefaultImage = (event) => {
  event.imageSrc = runtimeConfig.public.defaultProductImage;
};

const totalAmount = computed(() => {
  const price = currentVariant.value?.price ?? props.data.price;
  return parseFloat(price).toFixed(2);
});

const cartField = props.parent ? 'parent_item_id' : 'sku_id';
const cartItems = computed(() => cart.items.filter(item => item[cartField] === props.data[cartField]));

const cartItemQty = computed(() =>
  cartItems.value.reduce((total, item) => total + item.qty, 0)
);

const getButtonStyle = (variant, value, label) => {
  const isSelected = currentVariant.value?.[variant.label] === value;
  let isHighlighted = false;
  const foundVariant = variants.value?.[0]?.products.filter((product) => product[variant.label] === value);
  const excludedKeys = ['product_id', 'sku_id', label];
  const variantKeys = currentVariant.value
    ? Object.keys(currentVariant.value).filter((key) => !excludedKeys.includes(key))
    : [];

  for (const key of variantKeys) {
    const valueToCheck = currentVariant.value[key];
    const matchingVariant = foundVariant.find((product) => product[key] === valueToCheck);
    if (matchingVariant) {
      isHighlighted = true;
      break;
    }
  }

  return {
    backgroundColor: variant.label === 'colour' ? value : '',
    minWidth: variant.label === 'colour' ? '35px' : '150px',
    minHeight: '35px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: variant.label === 'colour' ? '35px' : '150px',
    borderRadius: variant.label === 'colour' ? '50%' : '4px',
    width: variant.label === 'colour' ? '45px' : '',
    height: variant.label === 'colour' ? '35px' : '',
    paddingLeft: '20px',
    paddingRight: '10px',
  };
};

const selectVariant = async (data, value, label) => {
  const foundVariant = variants.value?.[0]?.products.filter((product) => product[data.label] === value);
  const excludedKeys = ['product_id', 'sku_id', label];
  const variantKeys = currentVariant.value
    ? Object.keys(currentVariant.value).filter((key) => !excludedKeys.includes(key))
    : [];

  for (const key of variantKeys) {
    const valueToCheck = currentVariant.value[key];
    const matchingVariant = foundVariant.find((product) => product[key] === valueToCheck);
    if (matchingVariant) {
      GoogleAnalytics.cta({
        [EventParameter.CTA_NAME]: 'variant_select',
        [EventParameter.PRODUCT_SKU]: matchingVariant.sku_id,
      })
      productSku.value = matchingVariant.sku_id
      totalAmount.value = matchingVariant.price
      await fetchVariants(productSku.value);
      return;
    }
  }

  if (foundVariant?.[0]) {
    GoogleAnalytics.cta({
        [EventParameter.CTA_NAME]: 'variant_select',
        [EventParameter.PRODUCT_SKU]: foundVariant[0].sku_id,
      })
    productSku.value = foundVariant[0].sku_id
    totalAmount.value = foundVariant[0].price
    await fetchVariants(productSku.value);
  }
};

const currentImageUrl = ref('');
const lightbox = ref(null);
const sizeChartImages = ref([]);

const showPopup = async (imageValues) => {
  sizeChartImages.value = imageValues.map((item) => ({ url: item }));
  currentImageUrl.value = imageValues[0];
  if (lightbox.value) {
    lightbox.value.show(0);
  }
};

onMounted(() => {
  fetchVariants(productSku.value);
});

watch(
  () => props.data.sku_id,
  (newSku) => {
    productSku.value = newSku;
    fetchVariants(newSku);
  }
);

const handleAddToCart = async () => {
  try {
    await addToCart(); // Call the add-to-cart logic
  } catch (error) {
    console.error('Error adding to cart:', error);
  } 
};

const addToCart = async () => {

  if ( cartItemQty?.value > 0 && cartItems?.value?.[0]?.stock?.maximum &&
        cartItemQty?.value === cartItems?.value?.[0]?.stock?.maximum) {
          
    const message = `You’ve reached the maximum quantity of [${cartItems?.value?.[0]?.stock?.maximum}] allowed for this item: ${props?.data?.name || 'this item'}`;

    if (isMobile) {
      slideover.open(V3PopupComponent, {
        component: "error",
        data: { message },
        onClose: slideover.close,
      });
    } else {
      modal.open(V3ModalComponent, {
        component: "error",
        data: { message },
        onClose: modal.close,
      });
    }

    return;
  }
  
  if(props.data.sku_id !== productSku.value) {
    const result = await useAPIHandler('get', `/products/${productSku.value}`, {
      query: { partner_id: useRuntimeConfig().public.partnerCode },
    });
    const productData = result.data || {};
    cart.addItem({ ...productData, parent_item_id: productSku.value });
  } else {
    cart.addItem({ ...props.data, parent_item_id: productSku.value });
  }
}

const reduceQty = (parentId) => {
  cart.reduceQty(parentId);
};

const cartItemsData = computed(() => cart.getCartItems);
const cartItem = computed(() => {
  const item = cartItemsData.value.find(obj => obj.sku_id == productSku.value);
  return item || {};
});
</script>
